import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/auth.store';

// Dev: Vite proxy on /api/v1. Prod: same origin, VITE_API_URL=/api/v1.
const BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

export const api = axios.create({ baseURL: BASE_URL, timeout: 30_000 });
const refreshInstance = axios.create({ baseURL: BASE_URL });

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
  }
}

let isRefreshing = false;
let queue: { resolve: (t: string) => void; reject: (e: unknown) => void }[] = [];
const flush = (err: unknown, token: string | null) => {
  queue.forEach(({ resolve, reject }) => (err ? reject(err) : resolve(token!)));
  queue = [];
};

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const isAuthRoute = config.url?.startsWith('/auth/');
  const token = useAuthStore.getState().accessToken;
  if (token && config.headers && !isAuthRoute) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Unwrap the { success, data } envelope; refresh the session once on 401.
api.interceptors.response.use(
  (response) => response.data?.data ?? response.data,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status ?? 0;
    const isAdminCall = Boolean(original?.url?.startsWith('/admin') || original?.url === '/auth/me');

    if (status === 401 && isAdminCall && !original._retry) {
      const refreshToken = useAuthStore.getState().refreshToken;
      if (!refreshToken) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(error);
      }
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue.push({
            resolve: (token) => {
              original.headers!.Authorization = `Bearer ${token}`;
              resolve(api(original));
            },
            reject,
          });
        });
      }
      original._retry = true;
      isRefreshing = true;
      try {
        const res = await refreshInstance.post('/auth/refresh', { refreshToken });
        const { accessToken, refreshToken: next } = res.data.data as { accessToken: string; refreshToken: string };
        useAuthStore.getState().setAuth(accessToken, next, useAuthStore.getState().user!);
        flush(null, accessToken);
        original.headers!.Authorization = `Bearer ${accessToken}`;
        return api(original);
      } catch (e) {
        flush(e, null);
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }

    const body = error.response?.data as { message?: string | string[] } | undefined;
    const raw = body?.message ?? error.message ?? 'Unexpected error';
    const message = Array.isArray(raw) ? raw.join(', ') : raw;
    // Backend error codes are UPPER_SNAKE strings (e.g. RATE_LIMITED); keep them for i18n lookup.
    const code = /^[A-Z_]+$/.test(message) ? message : status === 429 ? 'RATE_LIMITED' : undefined;
    return Promise.reject(new ApiError(message, status, code));
  },
);

export default api;
